import React, { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GlobalPromoBanner from "@/components/GlobalPromoBanner";
import EnquiryModal from "@/components/EnquiryModal";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

import HomePage from "@/pages/HomePage";
import OurStoryPage from "@/pages/OurStoryPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ReviewsPage from "@/pages/ReviewsPage";
import FaqPage from "@/pages/FaqPage";
import ContactPage from "@/pages/ContactPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderConfirmationPage from "@/pages/OrderConfirmationPage";
import LegalPage from "@/pages/LegalPage";
import AdminLegalPagesPage from "@/pages/AdminLegalPagesPage";
import AdminLogoPage from "@/pages/AdminLogoPage";
import AdminProductsPage from "@/pages/AdminProductsPage";
import AdminContentPage from "@/pages/AdminContentPage";
import AdminPage from "@/components/AdminPage";
import AdminBannersPage from "@/pages/AdminBannersPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminInventoryPage from "@/pages/AdminInventoryPage";
import {
  AdminShippingPage, AdminTaxPage, AdminPaymentsPage,
  AdminBrandPage, AdminSeoPage, AdminGlobalPage,
} from "@/pages/AdminSettingsPages";
import {
  AdminFaqsPage, AdminBotanicalsPage, AdminIngredientsPage,
  AdminCategoriesPage, AdminReviewsPage, AdminCouponsPage,
  AdminInfluencersPage, AdminBlogPage, AdminMediaPage,
  AdminOrdersPage, AdminCustomersPage, AdminAuditPage,
  AdminRolesPage, AdminReportsPage, AdminAnalyticsPage,
  AdminIntegrationsPage,
} from "@/pages/AdminSimplePages";
import { CustomerLoginPage, CustomerSignupPage, CustomerForgotPasswordPage } from "@/pages/CustomerAuthPages";
import { CustomerAccountPage } from "@/pages/CustomerAccountPage";

import { CartProvider } from "@/context/CartContext";
import { BannerProvider } from "@/context/BannerContext";
import { ProductsProvider } from "@/context/ProductsContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { PageContentProvider } from "@/context/PageContentContext";
import { BrandProvider } from "@/context/BrandContext";
import { MetaPixelProvider } from "@/context/MetaPixelContext";
import { AnalyticsProvider } from "@/context/AnalyticsContext";
import TrackOrderPage from "@/pages/TrackOrderPage";
import DeliveryPage from "@/pages/DeliveryPage";
import AdminLogisticsPage from "@/pages/AdminLogisticsPage";
import AdminSubscribersPage from "@/pages/AdminSubscribersPage";
import EmailCapturePopup from "@/components/EmailCapturePopup";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);
  return null;
};

const EnquiryContext = React.createContext(null);
export const useEnquiry = () => React.useContext(EnquiryContext);

const Shell = ({ children }) => {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [defaultProduct, setDefaultProduct] = useState("Sea Buckthorn Pure Pulp");
  const openEnquiry = useCallback((product) => {
    setDefaultProduct(typeof product === "string" ? product : "Sea Buckthorn Pure Pulp");
    setEnquiryOpen(true);
  }, []);
  return (
    <EnquiryContext.Provider value={{ openEnquiry }}>
      <div className="min-h-screen flex flex-col bg-[var(--hk-ivory)]">
        <GlobalPromoBanner />
        <Header onEnquire={openEnquiry} />
        <div className="flex-1">{children}</div>
        <Footer onEnquire={openEnquiry} />
        <FloatingWhatsApp />
        <EmailCapturePopup />
        <EnquiryModal open={enquiryOpen} onOpenChange={setEnquiryOpen} defaultProduct={defaultProduct} />
      </div>
    </EnquiryContext.Provider>
  );
};

const StorefrontRoutes = () => (
  <Shell>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/our-story" element={<OurStoryPage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:slug" element={<ProductDetailPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/order/:orderId" element={<OrderConfirmationPage />} />
      <Route path="/refund-policy" element={<LegalPage slug="refund-cancellation-policy" />} />
      <Route path="/terms-and-conditions" element={<LegalPage slug="terms-and-conditions" />} />
      <Route path="/privacy-policy" element={<LegalPage slug="privacy-policy" />} />
      <Route path="/shipping-policy" element={<LegalPage slug="shipping-policy" />} />
      <Route path="/track-order" element={<TrackOrderPage />} />
      <Route path="/delivery" element={<DeliveryPage />} />
      <Route path="/login" element={<CustomerLoginPage />} />
      <Route path="/signup" element={<CustomerSignupPage />} />
      <Route path="/forgot-password" element={<CustomerForgotPasswordPage />} />
      <Route path="/account" element={<CustomerAccountPage />} />
    </Routes>
  </Shell>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BrandProvider>
          <BannerProvider>
            <ProductsProvider>
              <PageContentProvider>
                <CustomerAuthProvider>
                  <MetaPixelProvider>
                  <AnalyticsProvider>
                  <CartProvider>
                    <ScrollToTop />
                  <Routes>
                    {/* ============ Admin Console (full sidebar) ============ */}
                    <Route path="/admin" element={<AdminDashboardPage />} />
                    <Route path="/admin/enquiries" element={<AdminPage />} />
                    <Route path="/admin/products" element={<AdminProductsPage />} />
                    <Route path="/admin/banners" element={<AdminBannersPage />} />
                    <Route path="/admin/pages" element={<AdminContentPage />} />
                    <Route path="/admin/content" element={<AdminContentPage />} />
                    <Route path="/admin/homepage" element={<AdminContentPage />} />
                    <Route path="/admin/inventory" element={<AdminInventoryPage />} />
                    <Route path="/admin/orders" element={<AdminOrdersPage />} />
                    <Route path="/admin/customers" element={<AdminCustomersPage />} />
                    <Route path="/admin/users" element={<AdminCustomersPage />} />
                    <Route path="/admin/reviews" element={<AdminReviewsPage />} />
                    <Route path="/admin/faqs" element={<AdminFaqsPage />} />
                    <Route path="/admin/botanicals" element={<AdminBotanicalsPage />} />
                    <Route path="/admin/ingredients" element={<AdminIngredientsPage />} />
                    <Route path="/admin/categories" element={<AdminCategoriesPage kind="category" />} />
                    <Route path="/admin/collections" element={<AdminCategoriesPage kind="collection" />} />
                    <Route path="/admin/coupons" element={<AdminCouponsPage />} />
                    <Route path="/admin/influencers" element={<AdminInfluencersPage />} />
                    <Route path="/admin/blog" element={<AdminBlogPage />} />
                    <Route path="/admin/media" element={<AdminMediaPage />} />
                    <Route path="/admin/shipping" element={<AdminShippingPage />} />
                    <Route path="/admin/tax" element={<AdminTaxPage />} />
                    <Route path="/admin/payments" element={<AdminPaymentsPage />} />
                    <Route path="/admin/brand" element={<AdminBrandPage />} />
                    <Route path="/admin/seo" element={<AdminSeoPage />} />
                    <Route path="/admin/settings" element={<AdminGlobalPage />} />
                    <Route path="/admin/roles" element={<AdminRolesPage />} />
                    <Route path="/admin/reports" element={<AdminReportsPage />} />
                    <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                    <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
                    <Route path="/admin/logistics" element={<AdminLogisticsPage />} />
                    <Route path="/admin/subscribers" element={<AdminSubscribersPage />} />
                    <Route path="/admin/legal" element={<AdminLegalPagesPage />} />
                    <Route path="/admin/logo" element={<AdminLogoPage />} />
                    <Route path="/admin/logs" element={<AdminAuditPage />} />
                    <Route path="/*" element={<StorefrontRoutes />} />
                  </Routes>
                  </CartProvider>
                  </AnalyticsProvider>
                  </MetaPixelProvider>
                </CustomerAuthProvider>
              </PageContentProvider>
            </ProductsProvider>
          </BannerProvider>
        </BrandProvider>
      </BrowserRouter>
      <Toaster
        position="top-right"
        closeButton
        expand={false}
        gap={12}
        offset={20}
        toastOptions={{
          duration: 4500,
          classNames: {
            toast: "hk-toast",
            title: "hk-toast-title",
            description: "hk-toast-desc",
            success: "hk-toast--success",
            error: "hk-toast--error",
            info: "hk-toast--info",
            warning: "hk-toast--warning",
            closeButton: "hk-toast-close",
          },
        }}
      />
    </div>
  );
}

export default App;
